import { Injectable } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';

import { AreaProperty } from '@models/area-property.model';

/** Resultado de localizar un nodo en el árbol */
export interface NodeLocation {
  node: AreaProperty;
  parent: AreaProperty | null;
  list: AreaProperty[];
}

/**
 * Operaciones puras sobre el árbol de propiedades.
 * No depende del DOM ni de formularios Angular — solo recibe y devuelve datos.
 */
@Injectable({ providedIn: 'root' })
export class AreaPropertyTreeService {

  // ────────────────────────────────────────────────
  // Construcción del árbol
  // ────────────────────────────────────────────────

  /** Convierte una lista plana con parentId → árbol con children, ordenado por orderIndex */
  buildTree(flatList: AreaProperty[]): AreaProperty[] {
    if (!flatList?.length) return [];

    const map = new Map<string, AreaProperty>();
    const roots: AreaProperty[] = [];

    flatList.forEach(p => map.set(p.id, { ...p, children: [] } as AreaProperty));

    flatList.forEach(p => {
      const node = map.get(p.id)!;
      if (p.parentId && map.has(p.parentId)) {
        map.get(p.parentId)!.children!.push(node);
      } else {
        roots.push(node);
      }
    });

    this.sortTreeRecursively(roots);
    return roots;
  }

  private sortTreeRecursively(list: AreaProperty[]): void {
    list.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
    list.forEach(node => node.children?.length && this.sortTreeRecursively(node.children));
  }

  // ────────────────────────────────────────────────
  // Aplanar el árbol
  // ────────────────────────────────────────────────

  /** Recorre el árbol y devuelve la lista plana con parentId y orderIndex actualizados */
  flattenTree(tree: AreaProperty[]): AreaProperty[] {
    const flat: AreaProperty[] = [];

    const walk = (list: AreaProperty[], parentId: string | null) => {
      list.forEach((node, index) => {
        node.parentId = parentId;
        node.orderIndex = index + 1;

        const { children, ...rest } = node as any;
        flat.push(rest as AreaProperty);

        if (node.children?.length) {
          walk(node.children, node.id);
        }
      });
    };

    walk(tree, null);
    return flat;
  }

  // ────────────────────────────────────────────────
  // Búsqueda en el árbol
  // ────────────────────────────────────────────────

  /** Busca un nodo por id y devuelve el nodo, su padre y el array donde vive */
  locateNodeInTree(
    id: string,
    list: AreaProperty[],
    parent: AreaProperty | null = null
  ): NodeLocation | null {
    for (const node of list) {
      if (node.id === id) {
        return { node, parent, list };
      }
      if (node.children?.length) {
        const found = this.locateNodeInTree(id, node.children, node);
        if (found) return found;
      }
    }
    return null;
  }

  /** Encuentra el nodo padre cuyo array children es exactamente la referencia dada (usado en drag & drop) */
  findParentByChildrenRef(
    list: AreaProperty[],
    childrenRef: AreaProperty[]
  ): AreaProperty | null {
    for (const node of list) {
      if (node.children === childrenRef) return node;
      if (node.children?.length) {
        const found = this.findParentByChildrenRef(node.children, childrenRef);
        if (found) return found;
      }
    }
    return null;
  }

  // ────────────────────────────────────────────────
  // Modificación del árbol
  // ────────────────────────────────────────────────

  /** Elimina un nodo del árbol por su id. Devuelve true si lo encontró */
  removeNodeById(id: string, list: AreaProperty[]): boolean {
    const idx = list.findIndex(n => n.id === id);
    if (idx > -1) {
      list.splice(idx, 1);
      return true;
    }
    for (const node of list) {
      if (node.children && this.removeNodeById(id, node.children)) return true;
    }
    return false;
  }

  /** Recoge en acc los ids de todos los descendientes de un nodo (para evitar ciclos) */
  collectDescendantIds(node: AreaProperty, acc: Set<string>): void {
    if (!node.children) return;
    for (const child of node.children) {
      acc.add(child.id);
      this.collectDescendantIds(child, acc);
    }
  }

  // ────────────────────────────────────────────────
  // Utilidades
  // ────────────────────────────────────────────────

  /** Clona una propiedad con todos sus hijos, asignando nuevos ids y keyName únicos */
  clonePropertyTree(prop: AreaProperty, isRoot = true): AreaProperty {
    return {
      ...prop,
      id: uuidv4(),
      // Cada nodo clonado necesita su propio keyName: si se reutilizara el del
      // original habría dos campos con la misma identidad.
      keyName: this.generateKeyName(prop.label),
      label: isRoot ? prop.label + ' (copia)' : prop.label,
      visibilityCondition: prop.visibilityCondition
        ? JSON.parse(JSON.stringify(prop.visibilityCondition))
        : null,
      children: (prop.children ?? []).map(child => this.clonePropertyTree(child, false)),
    };
  }

  /**
   * Genera un keyName a partir de la etiqueta.
   * El sufijo aleatorio (6 caracteres base36 ≈ 2 mil millones de combinaciones)
   * hace la colisión entre etiquetas iguales prácticamente imposible.
   */
  generateKeyName(label: string): string {
    const safeLabel = (label || 'propiedad')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^\w_]/g, '');

    const datePart = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const uniqueSuffix = Math.random().toString(36).slice(2, 8);

    return `${safeLabel}_${datePart}_${uniqueSuffix}`;
  }
}
